import { Badge } from '@/components/ui/shadcn/badge';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { formatCurrency } from '@/lib/utils';
import type { Project } from '@/types';
import { Link, router } from '@inertiajs/react';
import { Building2, Calendar, DollarSign, FileText, MapPin } from 'lucide-react';
import { useCallback, useRef } from 'react';

interface ProjectCardProps {
    project: Project;
}

export function ProjectCard({ project }: ProjectCardProps) {
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'bg-success-subtle text-success-subtle-foreground';
            case 'in_progress':
                return 'bg-info-subtle text-info-subtle-foreground';
            case 'on_hold':
                return 'bg-warning-subtle text-warning-subtle-foreground';
            case 'cancelled':
                return 'bg-danger-subtle text-danger-subtle-foreground';
            default:
                return 'bg-muted text-muted-foreground';
        }
    };

    const addressParts = [project.address_street, project.address_house_number, project.address_city, project.address_municipality].filter(Boolean);

    // Track touch events to distinguish scrolls from taps
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const isScrollingRef = useRef(false);

    // Handle touch start - record initial position
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch) {
            touchStartRef.current = { x: touch.clientX, y: touch.clientY };
            isScrollingRef.current = false;
        }
    }, []);

    // Handle touch move - detect if user is scrolling
    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!touchStartRef.current) return;

        const touch = e.touches[0];
        if (!touch) return;

        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
        const threshold = 10; // pixels - adjust if needed

        // If movement exceeds threshold, user is scrolling
        if (deltaX > threshold || deltaY > threshold) {
            isScrollingRef.current = true;
        }
    }, []);

    // Handle navigation - only navigate if it was a tap, not a scroll
    const handleNavigation = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            const isTouch = 'touches' in e || 'changedTouches' in e;

            if (isTouch || window.innerWidth < 768) {
                // If it was a scroll gesture, don't navigate
                if (isScrollingRef.current) {
                    touchStartRef.current = null;
                    isScrollingRef.current = false;
                    return;
                }

                e.preventDefault();
                e.stopPropagation();
                router.visit(`/projects/${project.id}`, {
                    preserveState: false,
                    preserveScroll: false,
                });
            }

            // Reset refs after handling
            touchStartRef.current = null;
            isScrollingRef.current = false;
        },
        [project.id],
    );

    // Ensure we have a valid ID
    if (!project?.id) {
        console.error('Project ID is missing:', project);
        return null;
    }

    return (
        <Link
            href={`/projects/${project.id}`}
            className="touch-target block"
            onClick={handleNavigation}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleNavigation}
        >
            <Card className="mb-2 cursor-pointer transition-shadow hover:shadow-sm">
                <CardContent className="p-3">
                    <div className="flex flex-col gap-2">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <h3 className="truncate text-sm font-semibold">{project.contract_number || project.id}</h3>
                                {project.contract_name && <p className="text-muted-foreground mt-0.5 text-xs">{project.contract_name}</p>}
                            </div>
                            <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
                                <Badge variant="outline" className={`px-1.5 py-0 text-xs ${getStatusColor(project.status)}`}>
                                    {project.status}
                                </Badge>
                            </div>
                        </div>

                        {/* Details */}
                        <div className="space-y-1">
                            {project.sales_price > 0 && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <DollarSign className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span>{formatCurrency(project.sales_price)}</span>
                                </div>
                            )}

                            {project.company && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{project.company.name}</span>
                                </div>
                            )}

                            {addressParts.length > 0 && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{addressParts.join(', ')}</span>
                                </div>
                            )}

                            {project.end_date && (
                                <div className="flex items-center gap-1">
                                    <Calendar className="text-muted-foreground h-3 w-3" />
                                    <span className="text-muted-foreground text-xs">End: {new Date(project.end_date).toLocaleDateString()}</span>
                                </div>
                            )}

                            {project.start_date && (
                                <div className="flex items-center gap-1">
                                    <Calendar className="text-muted-foreground h-3 w-3" />
                                    <span className="text-muted-foreground text-xs">Start: {new Date(project.start_date).toLocaleDateString()}</span>
                                </div>
                            )}

                            {project.manufacturing_number && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span>MFG: {project.manufacturing_number}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}
